"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import config from "@/config";
import { freeUserBeforeAfterExamples } from "@/libs/beforeAfterExamples";
import { PRICING_REVIEWS } from "@/libs/pricingReviews";
import { useExperiment } from "@/hooks/useExperiment";
import {
  PRICING_MOBILE_ORDER_V1_EXPERIMENT_ID,
  getDevExperimentOverride,
  resolvePricingMobileOrderVariant,
} from "@/libs/experiments";
import ButtonCheckout from "./ButtonCheckout";
import BeforeAfterMarqueeSlider from "./BeforeAfterMarqueeSlider";
import { getPurchaseContext } from "@/libs/ga";
import { trackFunnel } from "@/libs/analytics";
import {
  CHOOSER_IMAGE_MODELS,
  type ImageModelDef,
} from "@/libs/image-models";

// -- Types ----------------------------------------------------------
export type PricingV2Layout = "v2" | "v2_1";

interface PricingV2Props {
  currentUserPriceId?: string | null;
  /** A/B: v2 (control) vs v2_1 - drives hero copy, plan blurbs, and `layout` on funnel events. */
  layout?: PricingV2Layout;
}

interface CreditPackage {
  credits: number;
  basePrice: number;
  discountPercent: number;
  finalPrice: number;
  priceId: string;
  quantity: number;
  couponId?: string;
}

// -- Data -----------------------------------------------------------
const plans = config.stripe.plans.filter((plan) => !plan.isHidden);

/** Default subscriber top-up: smallest pack (150 credits → ~$12 once). */
const DEFAULT_CREDIT_TOPUP = 150;

/** Mobile-only card order for pricing_mobile_order_v1 (pro_first arm). */
function mobilePlanOrderClass(
  planName: string,
  mobileOrderVariant: string | null
): string {
  if (mobileOrderVariant !== "pro_first") return "";
  const orderByPlan: Record<string, string> = {
    Pro: "max-md:order-1",
    Starter: "max-md:order-2",
    Premium: "max-md:order-3",
  };
  return orderByPlan[planName] ?? "";
}

/** Secondary plan CTAs — outlined primary. Pro stays the filled button. */
const SECONDARY_CTA_CLASS =
  "w-full min-h-16 h-16 py-0 px-4 rounded-lg font-semibold transition-all cursor-pointer text-center text-base leading-none inline-flex items-center justify-center bg-white border-2 border-primary text-primary hover:bg-primary/5";

const calculateCreditPackages = (): CreditPackage[] =>
  config.stripe.creditTopups.packages.map((pkg) => {
    const basePrice = pkg.credits * config.stripe.creditTopups.baseRate;
    const discount = pkg.discount || 0;
    const finalPrice = basePrice * (1 - discount / 100);
    return {
      credits: pkg.credits,
      basePrice: Math.ceil(basePrice),
      discountPercent: discount,
      finalPrice: Math.ceil(finalPrice),
      priceId: config.stripe.creditTopups.priceId,
      quantity: pkg.credits,
      couponId: pkg.couponId || undefined,
    };
  });

// -- Feature lists with loss-aversion on Personal -------------------
type FeatureItem = {
  name: string;
  badge?: string;
  included: boolean;
  highlight?: boolean;
};

type ExtraPillDef = { label: string; className: string };
type PlanCopyData = {
  description: string;
  tagline: string;
  /** Render chooser models with the same icons/tags as the editor dropdown. */
  showChooserModels?: boolean;
  extraPills?: ExtraPillDef[];
  features: FeatureItem[];
};

const MODEL_TAG_TONE: Record<NonNullable<ImageModelDef["tagTone"]>, string> = {
  balanced: "bg-primary/10 text-primary",
  pro: "bg-amber-100 text-amber-700",
  new: "bg-emerald-100 text-emerald-700",
  fast: "bg-sky-100 text-sky-700",
  hot: "bg-orange-100 text-orange-700",
  good: "bg-slate-100 text-slate-700",
};

const HYPER_REALISM_PILL: ExtraPillDef = {
  label: "Hyper Realism",
  className:
    "bg-gradient-to-r from-orange-500 to-pink-600 text-white border border-white/20 shadow-md",
};

function PricingModelIcon({ model }: { model: ImageModelDef }) {
  return (
    <span
      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg ${model.iconClassName}`}
      aria-hidden="true"
    >
      <Image
        src={model.icon}
        alt=""
        width={20}
        height={20}
        unoptimized
        className="h-5 w-5 object-contain"
      />
    </span>
  );
}

function PricingModelTag({ model }: { model: ImageModelDef }) {
  if (!model.tag) return null;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        MODEL_TAG_TONE[model.tagTone ?? "new"]
      }`}
    >
      {model.tag}
    </span>
  );
}

function ChooserModelsList({ extraPills }: { extraPills?: ExtraPillDef[] }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-semibold text-base-content">AI Models Included:</p>
      {CHOOSER_IMAGE_MODELS.map((model) => (
        <div key={model.id} className="flex items-center gap-2">
          <PricingModelIcon model={model} />
          <span className="min-w-0 flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-base-content">
              {model.name}
            </span>
            <PricingModelTag model={model} />
          </span>
        </div>
      ))}
      {extraPills?.map((pill) => (
        <div key={pill.label} className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold tracking-wide ${pill.className}`}
          >
            {pill.label}
          </span>
        </div>
      ))}
    </div>
  );
}

const v2Plans: Record<string, PlanCopyData> = {
  Starter: {
    description: "For quick updates",
    tagline: "Get started with the basics",
    showChooserModels: true,
    features: [
      { name: "Viral selfies", included: true, highlight: true },
      { name: "AI Headshots - standard quality", included: true },
      { name: "180 credits monthly", included: true },
      { name: "All 60+ AI tools", included: true },
      { name: "1,172+ styles & presets", included: true },
      { name: "Credits rollover (up to 3 months)", included: true },
      { name: "Enhanced HD headshots", included: false },
    ],
  },
  Pro: {
    description: "When first impressions count",
    tagline: "Everything you need - chosen by most users",
    showChooserModels: true,
    extraPills: [HYPER_REALISM_PILL],
    features: [
      { name: "AI Headshots - Enhanced", included: true, badge: "HD", highlight: true },
      { name: "Viral selfies", included: true, highlight: true },
      { name: "Asset Manager", included: true, highlight: true },
      { name: "360 credits monthly", included: true},
      { name: "2x more credits vs Starter", included: true },
      { name: "All 60+ AI tools", included: true },
      { name: "1,172+ styles & presets", included: true },
      { name: "Credits rollover (up to 3 months)", included: true },
      { name: "Priority support", included: false },
    ],
  },
  Premium: {
    description: "For power users",
    tagline: "Maximum volume & upcoming pro features",
    features: [
      { name: "Everything in Pro", included: true, highlight: true },
      { name: "1,050 credits monthly", included: true },
      { name: "All 60+ AI tools", included: true },
      { name: "1,172+ styles & presets", included: true },
      { name: "Enhanced HD headshots", badge: "HD", included: true },
      { name: "Asset Manager", included: true },
      { name: "Batch processing", badge: "Coming Soon", included: true },
      { name: "Credits rollover (up to 3 months)", included: true },
      { name: "Priority support", included: true },
    ],
  },
};

/** V2.1 - pills + buffed feature lists for A/B test.
 *  HIERARCHY: pills → highlighted features → normal features → excluded features.
 *  Only REAL features or honest re-framings of existing capabilities. */
const v2_1Plans: Record<string, PlanCopyData> = {
  Starter: {
    description: "For quick updates",
    tagline: "Get started with the basics",
    showChooserModels: true,
    features: [
      // -- highlights (top) --
      { name: "180 credits monthly", included: true, highlight: true },
      { name: "Viral selfies", included: true, highlight: true },
      { name: "AI Headshots", included: true },
      { name: "Standard quality photos", included: true},
      // -- normal included --
      { name: "All 60+ AI tools", included: true },
      { name: "1,172+ styles & presets", included: true },
      { name: "Custom prompts & instructions", included: true },
      { name: "~15 sec processing", included: true },
      { name: "Unlimited downloads", included: true },
      { name: "No watermarks", included: true },
      { name: "Credits rollover (up to 3 months)", included: true },
      // -- excluded (bottom) --
      { name: "Hyper Realism™", included: false },
      { name: "Enhanced HD headshots", included: false },
      { name: "Asset Manager", included: false },
    ],
  },
  Pro: {
    description: "When first impressions count",
    tagline: "Best value plan for most users",
    showChooserModels: true,
    extraPills: [HYPER_REALISM_PILL],
    features: [
      // -- highlights (top) --
      { name: "360 credits monthly", included: true, highlight: true },
      { name: "AI Headshots - Enhanced", included: true, highlight: true, badge: "HD" },
      { name: "All 60+ AI tools", included: true, highlight: true },
      { name: "1,172+ styles & presets", included: true, highlight: true },
      { name: "Edit with AI Chat", included: true, highlight: true },
      { name: "Highest quality photos", included: true, highlight: true },
      { name: "Best likeness", included: true, highlight: true },
      { name: "Viral selfies", included: true, highlight: true },
      { name: "Asset Manager", included: true},
      // -- normal included --
      { name: "Custom prompts & instructions", included: true },
      { name: "Outfit & pose presets", included: true },
      { name: "Commercial use license", included: true },
      { name: "~15 sec processing", included: true },
      { name: "Unlimited downloads", included: true },
      { name: "No watermarks", included: true },
      { name: "Credits rollover (up to 3 months)", included: true },
    ],
  },
  Premium: {
    description: "Maximum creative power",
    tagline: "For teams and power users",
    features: [
      // -- highlights (top) --
      { name: "Everything in Pro", included: true, highlight: true },
      // -- normal included --
      { name: "1,050 credits monthly", included: true, highlight: true },
      { name: "6x more credits vs Starter", included: true },
      { name: "Batch processing", included: true, badge: "Coming Soon" },
      { name: "All 60+ AI tools", included: true },
      { name: "1,172+ styles & presets", included: true },
      { name: "Custom prompts & instructions", included: true },
      { name: "Outfit & pose presets", included: true },
      { name: "Commercial use license", included: true },
      { name: "~15 sec processing", included: true },
      { name: "Unlimited downloads", included: true },
      { name: "No watermarks", included: true },
      { name: "Credits rollover (up to 3 months)", included: true },
      { name: "Priority support", included: true },
    ],
  },
};

const planCopyForLayout = (layout: PricingV2Layout) =>
  layout === "v2_1" ? v2_1Plans : v2Plans;

const heroForLayout: Record<
  PricingV2Layout,
  { title: React.ReactNode; subtitle: string; trust: string[] }
> = {
  v2: {
    title: "Choose the right plan for you",
    subtitle:
      "Professional headshots and viral scroll-stopping profile pictures - powered by AI",
    trust: ["~15 sec processing", "Just 1 selfie needed", "Cancel anytime"],
  },
  v2_1: {
    title: <>Pick a plan and <span className="text-primary">start creating</span></>,
    subtitle:
      "AI headshots, viral avatars, and 27+ tools - one subscription, cancel anytime",
    trust: ["~15 sec processing", "Just 1 selfie needed", "Cancel anytime"],
  },
};

// -- Icons ----------------------------------------------------------
const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

// -- Reviews data (v2.1 social proof) -------------------------------
const V2_1_REVIEWS = PRICING_REVIEWS;

const REVIEW_AVATAR_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-rose-500",
  "bg-amber-500", "bg-cyan-500", "bg-pink-500", "bg-indigo-500",
  "bg-teal-500", "bg-orange-500", "bg-fuchsia-500", "bg-lime-600",
];

const StarSmall = () => (
  <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const ShieldCheckSmall = () => (
  <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

function ReviewCard({ review, idx }: { review: (typeof V2_1_REVIEWS)[number]; idx: number }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 w-[280px] min-w-[280px] h-[200px] flex flex-col flex-shrink-0 select-none">
      <div className="flex items-center gap-0.5 mb-2 flex-shrink-0">
        {Array.from({ length: 5 }).map((_, i) => (
          <StarSmall key={i} />
        ))}
      </div>
      <p className="text-sm text-gray-700 leading-relaxed line-clamp-3 overflow-hidden max-h-[4.5rem] mb-2 flex-shrink-0">
        {review.text}
      </p>
      <div className="flex items-center gap-2.5 mt-auto flex-shrink-0 pt-1">
        <div
          className={`w-8 h-8 rounded-full ${REVIEW_AVATAR_COLORS[idx % REVIEW_AVATAR_COLORS.length]} flex items-center justify-center text-white text-xs font-bold`}
        >
          {review.avatar}
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{review.name}</p>
          <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
            <ShieldCheckSmall />
            Verified User
          </span>
        </div>
      </div>
    </div>
  );
}

function ReviewsMarquee() {
  const doubled = [...V2_1_REVIEWS, ...V2_1_REVIEWS];
  return (
    <div className="relative overflow-hidden py-2 mb-8 md:mb-10">
      <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none" />
      <div
        className="pricing-reviews-track flex gap-4"
        style={{ animation: "pricing-reviews-scroll 50s linear infinite", width: "max-content" }}
      >
        {doubled.map((r, i) => (
          <ReviewCard key={`${r.avatar}-${i}`} review={r} idx={i} />
        ))}
      </div>
      <style>{`
        @keyframes pricing-reviews-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .pricing-reviews-track:hover { animation-play-state: paused; }
      `}</style>
    </div>
  );
}

/** Alternative v2.1 social proof layout: full-width wall of cards sliding upward. */
const REVIEWS_COL_COUNT = 6;
// Top offsets (rem) to stagger columns up/down for an organic feel
const REVIEWS_COL_OFFSETS = [0, 3.5, 1.5, 5, 2.5, 4.25];
// Slightly varied durations so columns drift at different speeds
const REVIEWS_COL_DURATIONS = [52, 60, 46, 58, 50, 64];

function ReviewsGridV21() {
  // Triple the source list so each column has enough cards to fill the viewport
  const tripled = [...V2_1_REVIEWS, ...V2_1_REVIEWS, ...V2_1_REVIEWS];

  // Distribute reviews round-robin into columns
  const columns = Array.from({ length: REVIEWS_COL_COUNT }, (_, ci) =>
    tripled.filter((_, i) => i % REVIEWS_COL_COUNT === ci),
  );

  return (
    <section className="w-full" aria-labelledby="pricing-v21-reviews-heading">
      <div className="mx-auto w-full max-w-5xl px-4 pt-16">
        <h2
          id="pricing-v21-reviews-heading"
          className="mb-2 text-center text-2xl font-bold text-base-content md:text-3xl"
        >
          Loved by people everywhere
        </h2>
        <p className="mx-auto mb-0 max-w-xl text-center text-sm text-base-content/60 md:text-base">
          Real feedback from users who upgraded their photos with PFPMaker.
        </p>
        <TrustWidgetsV21 variant="reviewsBanner" />
      </div>

      <div className="relative max-h-[64rem] w-full overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-28 bg-gradient-to-b from-white to-transparent" aria-hidden />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-28 bg-gradient-to-t from-white to-transparent" aria-hidden />

        {/* Horizontal flex of independent scrolling columns */}
        <div className="flex gap-4 px-4">
          {columns.map((colReviews, ci) => (
            // Static offset wrapper — shifts the column down without affecting animation
            <div key={ci} style={{ paddingTop: `${REVIEWS_COL_OFFSETS[ci]}rem` }} className="flex-1 min-w-[260px]">
              {/* Animated column — duplicated content for seamless infinite loop */}
              <div
                className="reviews-wall-col flex flex-col gap-4 will-change-transform"
                style={{ animationDuration: `${REVIEWS_COL_DURATIONS[ci]}s` }}
              >
                {[0, 1].map((dup) =>
                  colReviews.map((review, idx) => (
                    <div
                      key={`${dup}-${idx}`}
                      aria-hidden={dup === 1}
                      className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm select-none flex flex-col"
                    >
                      <div className="flex items-center gap-0.5 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <StarSmall key={i} />
                        ))}
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed line-clamp-4 mb-3 flex-1">
                        {review.text}
                      </p>
                      <div className="flex items-center gap-2.5 mt-auto pt-1">
                        <div
                          className={`w-8 h-8 rounded-full ${REVIEW_AVATAR_COLORS[(ci * 3 + idx) % REVIEW_AVATAR_COLORS.length]} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}
                        >
                          {review.avatar}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{review.name}</p>
                          <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                            <ShieldCheckSmall />
                            Verified User
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .reviews-wall-col {
          animation: pricing-reviews-col-up linear infinite;
        }
        @keyframes pricing-reviews-col-up {
          from { transform: translateY(0); }
          to   { transform: translateY(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .reviews-wall-col { animation: none; }
        }
      `}</style>
    </section>
  );
}

/** Static mosaic grid — card height driven by text length, CSS columns masonry. */
function ReviewsMosaicV21() {
  return (
    <section className="w-full pt-12 md:pt-16" aria-labelledby="pricing-v21-reviews-mosaic-heading">
      <div className="mx-auto w-full max-w-5xl px-4">
        <h2
          id="pricing-v21-reviews-mosaic-heading"
          className="mb-2 text-center text-2xl font-bold text-base-content md:text-3xl"
        >
          Loved by people everywhere
        </h2>
        <p className="mx-auto mb-0 max-w-xl text-center text-sm text-base-content/60 md:text-base">
          Real feedback from users who upgraded their photos with PFPMaker.
        </p>
        <TrustWidgetsV21 variant="reviewsBanner" />
      </div>

      {/* CSS columns masonry — cards break naturally by content height */}
      {/* Outer wrapper is relative so the fade pins to the visible bottom edge, not scroll content bottom */}
      <div className="relative mt-8 w-full">
        <div className="max-h-[64rem] overflow-y-scroll md:overflow-hidden">
          <div className="mx-auto w-full max-w-[1600px] px-4 [columns:280px_auto] [column-gap:1rem]">
            {V2_1_REVIEWS.map((review, idx) => (
              <div
                key={review.avatar + idx}
                className="mb-4 break-inside-avoid rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
              >
                <div className="mb-2 flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <StarSmall key={i} />
                  ))}
                </div>
                <p className="mb-3 text-sm leading-relaxed text-gray-700">{review.text}</p>
                <div className="flex items-center gap-2.5">
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${REVIEW_AVATAR_COLORS[idx % REVIEW_AVATAR_COLORS.length]}`}
                  >
                    {review.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{review.name}</p>
                    <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                      <ShieldCheckSmall />
                      Verified User
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Fade sits outside the scroll container — always visible at the bottom edge */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-48"
          style={{ background: "linear-gradient(to bottom, transparent, #f9fafb)" }}
          aria-hidden
        />
      </div>
    </section>
  );
}

/** Before/after marquee (same example assets as `FreeUserConversionModal` thumbnails). */
function PricingBeforeAfterSectionV21() {
  const pairs = freeUserBeforeAfterExamples.map((e) => ({ before: e.before, after: e.after }));

  return (
    <section className="w-full pt-12 md:pt-16" aria-labelledby="pricing-v21-before-after-heading">
      <div className="mx-auto w-full max-w-5xl px-4">
        <h2
          id="pricing-v21-before-after-heading"
          className="mb-2 text-center text-2xl font-bold text-base-content md:text-3xl"
        >
          See what you can create
        </h2>
        <p className="mx-auto mb-8 max-w-xl text-center text-sm text-base-content/60 md:text-base">
          Before and after examples from real PFPMaker results — same transformations as in our upgrade flow.
        </p>
      </div>
      <div className="w-full min-w-0">
        <BeforeAfterMarqueeSlider pairs={pairs} />
      </div>
    </section>
  );
}

/** Same star treatment as `AuthModal` `GlamTrustStars45` (4.5 stars). */
function PricingGlamTrustStars45() {
  const starPath =
    "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z";
  return (
    <div className="flex justify-center gap-1" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <svg key={i} className="h-6 w-6 text-amber-400 sm:h-7 sm:w-7" viewBox="0 0 20 20" fill="currentColor">
          <path d={starPath} />
        </svg>
      ))}
      <span className="relative h-6 w-6 shrink-0 sm:h-7 sm:w-7">
        <svg className="absolute inset-0 h-6 w-6 text-base-300 sm:h-7 sm:w-7" viewBox="0 0 20 20" fill="currentColor">
          <path d={starPath} />
        </svg>
        <span className="absolute inset-0 w-[50%] overflow-hidden">
          <svg className="h-6 w-6 text-amber-400 sm:h-7 sm:w-7" viewBox="0 0 20 20" fill="currentColor">
            <path d={starPath} />
          </svg>
        </span>
      </span>
    </div>
  );
}

/**
 * Laurel flanks matching Auth modal layout. File `laurel-leaf.svg` is white @ 55% for dark UI;
 * on light pricing we must use currentColor or leaves disappear.
 */
function PricingTrustLaurelFlank({ mirror }: { mirror?: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 96"
      aria-hidden
      className="block h-14 w-11 shrink-0 select-none text-neutral-400/70 sm:h-16 sm:w-12"
      style={mirror ? { transform: "scaleX(-1)" } : undefined}
    >
      <g fill="currentColor">
        <path d="M33.049,93.968c0.279-0.26,27.582-26.404,15.97-68.508c-0.079-0.288,0.087-0.587,0.375-0.668h0     c0.464-0.131,0.945,0.142,1.071,0.607C62.09,68.168,34.299,94.737,34.013,95L33.049,93.968z" />
        <path d="M62.966,69.752c-3.739,11.624-14.646,8.838-14.646,8.838C51.425,67.207,62.966,69.752,62.966,69.752z" />
        <path d="M53.175,64.9c13.853,1.513,14.092-11.143,14.092-11.143C53.97,53.515,53.175,64.9,53.175,64.9z" />
        <path d="M67.328,35.862c-1.455,15.974-12.39,14.283-12.39,14.283C54.045,35.246,67.328,35.862,67.328,35.862z" />
        <path d="M59.981,12.961c7.245,15.362-5.086,20.773-5.086,20.773C49.251,20.206,59.981,12.961,59.981,12.961z" />
        <path d="M44.389,5c11.938,6.447,4.858,18.348,4.858,18.348C36.428,12.325,44.389,5,44.389,5z" />
        <path d="M32.983,62.432c13.254,3.002,10.879,14.469,10.879,14.469C29.795,74.249,32.983,62.43,32.983,62.432z" />
        <path d="M36.022,47.268c12.815,3.82,11.94,15.681,11.94,15.681C35.226,63.268,36.022,47.268,36.022,47.268z" />
        <path d="M36.579,34.852c13.372,0.555,13.054,14.247,13.054,14.247C41.035,50.531,36.579,34.852,36.579,34.852z" />
        <path d="M36.579,20.92c12.178,1.991,11.779,12.667,11.779,12.667C36.736,32.805,36.579,20.92,36.579,20.92z" />
      </g>
    </svg>
  );
}

type TrustWidgetsV21Variant = "default" | "reviewsBanner";

/** Same layout as `AuthModal` `GlamExperimentTrustFooter`, plus reviews-banner variant. */
function TrustWidgetsV21({ variant = "default" }: { variant?: TrustWidgetsV21Variant }) {
  if (variant === "reviewsBanner") {
    const starPath = "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z";
    return (
      <div className="mx-auto mt-2 mb-10 flex w-full max-w-5xl flex-col items-center gap-3 pt-4">
        <p className="text-xl font-bold text-base-content md:text-2xl">
          18M+ active users worldwide
        </p>
        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-0.5" aria-hidden>
            {Array.from({ length: 5 }).map((_, i) => (
              <svg key={i} className="h-4 w-4 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path d={starPath} />
              </svg>
            ))}
          </div>
          <span className="text-sm font-medium text-base-content">Rated 4.6</span>
          <span className="text-base-content/30">·</span>
          <span className="text-sm text-base-content/60">70,519 reviews</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 flex max-w-xl mx-auto items-stretch gap-4 text-center sm:mt-4 sm:gap-6 sm:pt-3 mb-8">
      <div className="flex min-w-0 flex-1 flex-col items-center justify-end gap-2">
        <PricingGlamTrustStars45 />
        <p className="max-w-[14rem] text-sm leading-snug sm:max-w-none sm:text-base">
          <span className="font-bold text-base-content">Rated 4.5</span>
          <span className="text-base-content/60">{" · "}</span>
          <span className="font-normal text-base-content/60">70,519 reviews</span>
        </p>
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-center justify-end gap-0">
        <div className="flex items-center justify-center gap-1 leading-none -mb-[10px] sm:gap-1.5">
          <PricingTrustLaurelFlank mirror />
          <span className="text-2xl font-bold tabular-nums leading-none text-base-content sm:text-4xl">18M</span>
          <PricingTrustLaurelFlank />
        </div>
        <p className="mt-0 flex items-center justify-center gap-2 text-sm leading-tight text-base-content/60 sm:text-base">
          <span
            className="mt-[5px] inline-block h-2.5 w-2.5 shrink-0 animate-online-dot rounded-full bg-emerald-400 sm:h-2 sm:w-2"
            aria-hidden
          />
          <span>users worldwide</span>
        </p>
      </div>
    </div>
  );
}

// -- Main component -------------------------------------------------
const PricingV2 = ({
  currentUserPriceId = null,
  layout = "v2",
}: PricingV2Props) => {
  const [selectedOtherPack, setSelectedOtherPack] = useState(DEFAULT_CREDIT_TOPUP);
  const creditPackages = calculateCreditPackages();
  const planCopy = planCopyForLayout(layout);
  const hero = heroForLayout[layout];
  const { variant: assignedMobileOrder } = useExperiment(
    PRICING_MOBILE_ORDER_V1_EXPERIMENT_ID
  );
  const mobileOrderVariant = resolvePricingMobileOrderVariant(
    assignedMobileOrder ??
      getDevExperimentOverride(PRICING_MOBILE_ORDER_V1_EXPERIMENT_ID)
  );

  const existingContext = getPurchaseContext(false);
  const purchaseTrigger = existingContext?.purchase_trigger || "voluntary";
  const source = existingContext?.source || "direct";
  const hasActiveSubscription = !!currentUserPriceId;

  useEffect(() => {
    const justAuthenticated =
      typeof window !== "undefined" &&
      sessionStorage.getItem("pfp_just_authenticated");
    if (justAuthenticated) {
      sessionStorage.removeItem("pfp_just_authenticated");
    }
    trackFunnel("monetization", "pricing_page_loaded", {
      layout,
      has_current_subscription: hasActiveSubscription,
      current_price_id: currentUserPriceId,
      purchase_trigger: purchaseTrigger,
      source,
      is_post_auth: justAuthenticated === "true",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  const handleCreditSelect = (credits: number) => {
    setSelectedOtherPack(credits);
    const pkg = creditPackages.find((p) => p.credits === credits);
    trackFunnel("monetization", "credit_package_selected", {
      layout,
      credits,
      price: pkg?.finalPrice,
      discount: pkg?.discountPercent || 0,
      package_type: "topup",
    });
  };

  return (
    <section
      className="bg-gray-50 px-4 py-2 md:py-8"
      id="pricing"
      data-pricing-layout={layout}
    >
      <div className="mx-auto max-w-6xl">

        {/* --------------------------------------------------------
            HEADER
        -------------------------------------------------------- */}
        <div className="text-center mb-6">
          <h1 className="text-4xl md:text-5xl font-bold text-base-content mb-4">
            {hero.title}
          </h1>
          <p className="text-base-content/60 text-lg max-w-3xl mx-auto">
            {hero.subtitle}
          </p>
        </div>

        {/* Trust strip */}
        <div className="mb-6 md:mb-12">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-base-content/70">
            {hero.trust.map((t) => (
              <span key={t} className="flex items-center gap-1.5">
                <CheckIcon className="w-4 h-4 text-green-600" />
                {t}
              </span>
            ))}
          </div>
       {/* <TrustWidgetsV21 /> */}
        </div>

        {/* --------------------------------------------------------
            SUBSCRIPTION PLANS  (3 cards, push Standard)
        -------------------------------------------------------- */}
        <div className="grid gap-6 mx-auto items-stretch pt-0 md:pt-6 md:pt-0 md:grid-cols-3 max-w-6xl">
          {plans.map((plan) => {
            const v2 = planCopy[plan.name];
            if (!v2) return null;

            const isTarget = plan.isFeatured;
            const isPremium = plan.isMostPowerful;
            const isDiscountPlan = plan.name === "Pro" || plan.name === "Premium";
            const isPersonal = !isTarget && !isPremium;
            const priceIdToUse = plan.priceId;
            const isCurrentPlan = currentUserPriceId === priceIdToUse;
            // V2 anchor: Premium displayed at $35 to make Professional ($15) feel like obvious value.
            // Stripe price ID unchanged - actual charge stays $20 until a new price ID is configured.
            const displayPrice = isPremium ? 35 : plan.price;
            const beforePrice =
              typeof plan.priceAnchor === "number" && plan.priceAnchor > displayPrice
                ? plan.priceAnchor
                : null;
            const discountPercent =
              beforePrice && isDiscountPlan
                ? Math.round(((beforePrice - displayPrice) / beforePrice) * 100)
                : null;

            return (
              <div
                key={plan.name}
                data-plan={plan.name}
                className={`relative rounded-2xl flex flex-col ${mobilePlanOrderClass(
                  plan.name,
                  mobileOrderVariant
                )} ${
                  isTarget
                    ? "bg-base-100 border-2 border-primary shadow-xl shadow-primary/20 z-10 p-7 md:-my-1.5"
                    : isPersonal
                    ? "bg-base-100 border border-base-300 p-6 opacity-[0.92]"
                    : "bg-base-100 border border-base-300 p-6"
                }`}
              >
                {isTarget && (
                  <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-primary-content px-5 py-1.5 rounded-full text-sm font-bold whitespace-nowrap shadow-md">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-4">
                  <div className="mb-2 min-h-[28px]">
                    {discountPercent ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-black tracking-wide text-emerald-800">
                        {discountPercent}% OFF
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center rounded-full px-3 py-1 text-xs font-black tracking-wide opacity-0 select-none"
                        aria-hidden="true"
                      >
                        00% OFF
                      </span>
                    )}
                  </div>
                  <h3 className={`text-xl font-bold ${isTarget ? "text-primary" : "text-base-content"}`}>
                    {plan.name}
                  </h3>
                  <p className="text-base-content/50 text-sm mt-0.5">{v2.description}</p>
                </div>

                <div className="mb-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-base-content">${displayPrice}</span>
                    <span className="text-base-content/50 ml-1 text-base">/month</span>
                    {beforePrice && (
                      <span className="text-3xl font-bold text-base-content/30 line-through">
                        ${beforePrice}
                      </span>
                    )}
                  </div>
                </div>

                <p className={`text-xs mb-5 ${isTarget ? "text-primary/80 font-medium" : "text-base-content/40"}`}>
                  {v2.tagline}
                </p>

                {/* CTA */}
                <div className="mb-5">
                  {isCurrentPlan ? (
                    <Link href="/dashboard/account" className="btn btn-primary btn-block btn-lg min-h-16 h-16 text-base rounded-lg font-medium">
                      Manage Plan
                    </Link>
                  ) : hasActiveSubscription ? (
                    <button disabled className="btn btn-block btn-lg min-h-16 h-16 rounded-lg font-medium cursor-not-allowed !bg-gray-300 !border-gray-300 !text-gray-500 opacity-60">
                      Get {plan.name}
                    </button>
                  ) : isTarget ? (
                    /* Gradient border + shimmer wrapper for Get Pro */
                    <div className="get-pro-border-wrap relative w-full rounded-lg p-[2px]" style={{overflow: "hidden"}}>
                      {/* Rotating conic gradient — creates the animated glowing border */}
                      <div className="get-pro-border-ring absolute inset-[-150%]" aria-hidden />
                      <ButtonCheckout
                        priceId={priceIdToUse}
                        mode="subscription"
                        text="Get Pro"
                        className="get-pro-btn btn btn-primary btn-block btn-lg min-h-16 h-16 text-base rounded-[calc(0.5rem-2px)] font-bold transition-all relative z-10"
                        purchaseTrigger="voluntary"
                      />
                      {/* Shimmer — physical streak sliding left→right, clipped by overflow-hidden */}
                      <span className="pointer-events-none absolute inset-0 z-20 overflow-hidden rounded-[calc(0.5rem-2px)]" aria-hidden>
                        <span className="get-pro-shimmer-streak absolute inset-y-0 w-2/5" />
                      </span>
                    </div>
                  ) : (
                    <ButtonCheckout
                      priceId={priceIdToUse}
                      mode="subscription"
                      text={`Get ${plan.name}`}
                      className={SECONDARY_CTA_CLASS}
                      purchaseTrigger="voluntary"
                    />
                  )}
                </div>

                {(v2.showChooserModels || (v2.extraPills && v2.extraPills.length > 0)) && (
                  <div className="mb-3">
                    <ChooserModelsList extraPills={v2.extraPills} />
                  </div>
                )}

                {/* Features */}
                <ul className="space-y-2.5 flex-1">
                  {v2.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      {f.included ? (
                        <CheckIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${f.highlight ? "text-primary" : "text-green-500/70"}`} />
                      ) : (
                        <XIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-base-content/20" />
                      )}
                      <span className={`text-sm ${
                        !f.included ? "text-base-content/30 line-through"
                        : f.highlight ? "text-base-content font-medium"
                        : "text-base-content/70"
                      }`}>
                        {f.name}
                        {f.badge && f.included && (
                          <span className={`ml-1.5 inline-flex px-1.5 py-0.5 text-xs font-semibold rounded ${
                            f.badge === "HD" ? "bg-primary/10 text-primary" : "bg-blue-50 text-blue-600"
                          }`}>
                            {f.badge}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>

                {isPersonal && !hasActiveSubscription && (
                  <div className="mt-4 pt-3 border-t border-base-200">
                    <p className="text-xs text-primary text-center">
                      Upgrade to Pro for AI Headshots & 2x credits →
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>


        <div className="flex justify-center mt-8 mb-10 md:mt-10 md:mb-12">
          <img
            src="/assets/images/tools/payment-secure.png"
            alt="Secure Payment - Stripe Powered"
            className="h-24 opacity-70 hover:opacity-100 transition-opacity"
          />
        </div>


        {/* --------------------------------------------------------
            CREDIT TOP-UP - only shown to active subscribers
        -------------------------------------------------------- */}
        {hasActiveSubscription && <div className="max-w-md mx-auto mt-12 mb-16" id="credits">
          <p className="text-center text-base-content/40 text-sm mb-3">
            Need more credits?
          </p>
          <div className="bg-base-100 rounded-xl p-5 border border-base-300">
            <div className="space-y-1.5 mb-4">
              {creditPackages.map((pkg) => (
                <label
                  key={pkg.credits}
                  className={`flex items-center justify-between py-2 px-3 cursor-pointer rounded-lg transition-all border ${
                    selectedOtherPack === pkg.credits
                      ? "border-primary/40 bg-primary/5"
                      : "border-transparent hover:bg-base-200"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <input
                      type="radio"
                      name="creditPack"
                      value={pkg.credits}
                      checked={selectedOtherPack === pkg.credits}
                      onChange={() => handleCreditSelect(pkg.credits)}
                      className="radio radio-primary radio-sm"
                    />
                    <span className="text-base-content text-sm">
                      {pkg.credits} credits
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base-content font-semibold text-sm">${pkg.finalPrice}</span>
                    <span className="text-success text-xs w-12 text-right">
                      {pkg.discountPercent > 0 ? `${pkg.discountPercent}% off` : ""}
                    </span>
                  </div>
                </label>
              ))}
            </div>
            {(() => {
              const selected = creditPackages.find((p) => p.credits === selectedOtherPack);
              if (!selected) return null;
              return (
                <ButtonCheckout
                  priceId={selected.priceId}
                  quantity={selected.quantity}
                  couponId={selected.couponId}
                  mode="payment"
                  text={`Buy ${selected.credits} Credits - $${selected.finalPrice}`}
                  className={SECONDARY_CTA_CLASS}
                  purchaseTrigger={purchaseTrigger as "voluntary" | "forced"}
                />
              );
            })()}
            <p className="text-center text-base-content/30 text-xs mt-3">
              One-time payment · Never expires · All tools included
            </p>
            <div className="mt-4">
              <ChooserModelsList />
            </div>
          </div>
        </div>}

        {/* --------------------------------------------------------
            TRUST / SOCIAL PROOF
        -------------------------------------------------------- */}
        {layout === "v2_1" ? null : (
          <div className="text-center mb-12 md:mb-16">
            <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4">
              <div className="flex -space-x-2">
                {[1, 2, 3].map((n) => (
                  <img
                    key={n}
                    src={`/assets/images/tools/first-customers-${n}.png`}
                    alt="Customer"
                    className="w-8 h-8 rounded-full border-2 border-base-100"
                  />
                ))}
              </div>
              <p className="text-primary font-medium">Trusted by 18+ million users</p>
            </div>
          </div>
        )}
      </div>

      {layout === "v2_1" && (
        <>
          {/* <PricingBeforeAfterSectionV21 /> */}
          {/* <ReviewsGridV21 /> */}
          <ReviewsMosaicV21 />
        </>
      )}

      <div className="mx-auto max-w-6xl">
        {/* --------------------------------------------------------
            STATS
        -------------------------------------------------------- */}
        {/*
        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 border border-primary/10 mb-16">
          <h3 className="text-2xl font-bold text-center text-base-content mb-2">Why Millions Choose PFPMaker</h3>
          <p className="text-center text-base-content/60 text-sm mb-8">The world&apos;s most popular profile picture platform since 2020</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { val: "18M+", label: "Users Worldwide" },
              { val: "55M+", label: "Profile Pictures Downloaded" },
              { val: "1,172+", label: "AI Styles" },
              { val: "~15s", label: "Avg processing time" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-4xl font-bold text-primary mb-1">{s.val}</div>
                <div className="text-sm text-base-content/70">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        */}

        {/* --------------------------------------------------------
            ALL PLANS INCLUDE
        -------------------------------------------------------- */}
        {/*}
        <div className="bg-base-100 rounded-2xl p-8 border border-base-300 mb-16">
          <h3 className="text-2xl font-bold text-center text-base-content mb-6">Every Plan Includes</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "20 Advanced AI Tools", desc: "Headshots, avatars, background removal, clothing changes, hair styling, makeup, and more" },
              { title: "7 Free PFP Editing Tools", desc: "Remove backgrounds, crop & resize, add borders, text, effects, decals, and custom backgrounds" },
              { title: "Hundreds of Presets", desc: "Professional backgrounds, creative styles, borders, effects, and customizable templates" },
              { title: "Privacy Focused", desc: "Encrypted storage, no AI training on your photos, your data stays private" },
              { title: "Lightning Fast", desc: "~15 second average processing - results from just 1 photo" },
              { title: "Flexible Credits", desc: "Subscription credits rollover up to 3x your plan limit. Purchased credits never expire" },
            ].map((item) => (
              <div key={item.title}>
                <h4 className="font-semibold text-base-content mb-3 flex items-center gap-2">
                  <CheckIcon className="w-5 h-5 text-primary" />
                  {item.title}
                </h4>
                <p className="text-sm text-base-content/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
        */}

        {/* --------------------------------------------------------
            FAQ
        -------------------------------------------------------- */}
        <div className="max-w-4xl mx-auto pb-0 pt-16">
          <h2 className="text-3xl font-bold text-center text-base-content mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              { q: "What can I create with PFPMaker?", a: "PFPMaker gives you 27 AI tools with 1,172+ styles. Generate professional headshots for LinkedIn, creative avatars for gaming and social media, change backgrounds, hairstyles, outfits, add accessories - all from a single selfie. You can customize every detail after generation using our free editing tools." },
              { q: "What's the difference between Standard and Enhanced quality?", a: "Standard quality delivers great results for most use cases. Enhanced quality (available on Pro and Premium plans) applies advanced upscaling and detail refinement for ultra-sharp, professional-grade headshots - ideal for LinkedIn, business cards, and anywhere your photo needs to look pixel-perfect." },
              { q: "Can I buy credits without a subscription?", a: "No — credit packs are only available to active subscribers and never expire. They're perfect for topping up when you need a burst of extra credits. Subscriptions offer better value per credit and include exclusive features like Asset Manager and Enhanced HD quality (on Pro)." },
              { q: "Do subscription credits expire?", a: "Subscription credits rollover up to 3x your plan limit. For example, Pro (360 credits/month) can accumulate up to 1,080 credits. Purchased credit packs never expire." },
              { q: "What if the AI fails or I don't like the result?", a: "If the AI fails to process your image (technical error), credits are automatically refunded immediately. If you're not satisfied, try different settings or styles - with 1,172+ options there's always a look that fits." },
              { q: "Can I cancel anytime?", a: "Yes. Cancel from your dashboard at any time. You keep access until the end of your billing period, and accumulated credits remain available. No fees, no questions." },
              { q: "Is my data private?", a: "Absolutely. Your images are stored in encrypted storage with industry-standard security. We never train AI on your photos and never add watermarks." },
            ].map((faq) => (
              <details key={faq.q} className="group bg-base-100 rounded-lg border border-base-300 p-6">
                <summary className="font-semibold text-base-content cursor-pointer list-none flex items-center justify-between">
                  <span>{faq.q}</span>
                  <svg className="w-5 h-5 transition-transform group-open:rotate-180 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-base-content/90 text-sm">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        /* ── Get Pro animated gradient border ── */
        .get-pro-border-wrap {
          background: transparent;
        }
        /* Button: light primary → primary → dark primary gradient */
        .get-pro-btn {
        /*
        background: linear-gradient(
            145deg,
            color-mix(in oklch, oklch(var(--p)), white 18%) 0%,
            oklch(var(--p)) 55%,
            color-mix(in oklch, oklch(var(--p)), black 18%) 100%
          ) !important;
           */
          border-color: transparent !important;
        }
        /* ↓↓ BORDER ANIMATION COLORS — edit the hex values below ↓↓ */
        /*
        .get-pro-border-ring {
          background: conic-gradient(
            from 0deg at 50% 50%,
            #8b5cf6  0%,
            #06b6d4 28%,
            #3b82f6 50%,
            #8b5cf6 72%,
            #a78bfa 88%,
            #8b5cf6 100%
          );
          */
        .get-pro-border-ring {
          background: #ffffff;
          background: #5956e9;
          animation: none;
        }
        @keyframes get-pro-ring-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        /* Shimmer — translate-based, no background-position tricks */
        .get-pro-shimmer-streak {
          left: -40%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.15) 35%,
            rgba(255,255,255,0.30) 50%,
            rgba(255,255,255,0.15) 65%,
            transparent 100%
          );
          animation: get-pro-streak-slide 2.4s ease-in-out infinite;
        }
        @keyframes get-pro-streak-slide {
          0%   { transform: translateX(-100%); }
          60%  { transform: translateX(450%); }
          100% { transform: translateX(450%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .get-pro-border-ring    { animation: none; }
          .get-pro-shimmer-streak { animation: none; }
        }
      `}</style>
    </section>
  );
};

// -- Comparison row helper ------------------------------------------
function CompRow({ label, values, targetCol, isBool, bold }: {
  label: string; values: string[]; targetCol?: number; isBool?: boolean; bold?: boolean;
}) {
  return (
    <tr>
      <td className="py-3 pr-4 text-base-content/60">{label}</td>
      {values.map((val, i) => {
        const isTarget = i === targetCol;
        const isYes = val === "yes";
        const isNo = val === "no";
        return (
          <td key={i} className={`text-center py-3 px-3 ${bold ? "font-semibold" : ""} ${
            isTarget && !isNo ? "text-primary font-medium" : isNo ? "text-base-content/20" : "text-base-content/70"
          }`}>
            {isBool && isYes ? <CheckIcon className="w-5 h-5 text-primary mx-auto" />
              : isNo ? <span className="text-base-content/20">-</span>
              : val}
          </td>
        );
      })}
    </tr>
  );
}

export default PricingV2;
